import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, getWorkers, createWorker, updateWorker, deleteWorker } from '../api';
import Layout from './Layout';
import DataTable from './DataTable';
import WorkersSkeleton from './skeletons/WorkersSkeleton';
import { formatDateTimeEST, formatDateEST } from '../utils/dateUtils';
import './AdminDashboard.css';
import './AdminWorkers.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function Workers({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('statistics'); // 'statistics' or 'accounts'

  // Statistics tab state
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Accounts tab state
  const [workerAccounts, setWorkerAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone: '',
    status: 'active',
    new_password: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'statistics') {
      loadWorkers();
    } else if (activeTab === 'accounts') {
      fetchWorkerAccounts();
    }
  }, [activeTab]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Statistics functions
  const loadWorkers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/workers.php`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();

      if (data.success) {
        setWorkers(data.workers || []);
      } else {
        setError(data.error || 'Failed to load workers');
        if (data.error?.includes('Unauthorized')) {
          handleLogout();
        }
      }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred';
      setError(`Error loading workers: ${errorMessage}`);
      console.error('Load workers error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = async (workerName, submissionCount) => {
    const confirmMessage = `Are you sure you want to delete "${workerName}"?\n\nThis will permanently delete:\n- The worker\n- All ${submissionCount} submissions from this worker\n- All associated files\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/delete-worker.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ worker_name: workerName })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Successfully deleted "${workerName}" and ${data.deleted_submissions} submissions`);
        loadWorkers();
      } else {
        alert(`Failed to delete worker: ${data.error}`);
      }
    } catch (error) {
      alert('An error occurred while deleting the worker');
      console.error('Delete worker error:', error);
    }
  };

  // Accounts functions
  const fetchWorkerAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await getWorkers();
      if (response.success) {
        setWorkerAccounts(response.workers);
      }
    } catch (err) {
      setAccountsError(err.message || 'Failed to load worker accounts');
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'new_password' || name === 'password') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength('');
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    if (strength <= 2) setPasswordStrength('weak');
    else if (strength <= 3) setPasswordStrength('medium');
    else setPasswordStrength('strong');
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';

    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];

    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    password = password.split('').sort(() => Math.random() - 0.5).join('');

    const fieldName = editingWorker ? 'new_password' : 'password';
    setFormData(prev => ({
      ...prev,
      [fieldName]: password
    }));
    checkPasswordStrength(password);

    setMessage({ type: 'success', text: `Password generated: ${password}` });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage({ type: 'success', text: 'Password copied to clipboard!' });
    }).catch(() => {
      setMessage({ type: 'error', text: 'Failed to copy password' });
    });
  };

  const openCreateModal = () => {
    setEditingWorker(null);
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      status: 'active',
      new_password: ''
    });
    setShowModal(true);
  };

  const openEditModal = (worker) => {
    setEditingWorker(worker);
    setFormData({
      username: worker.username,
      password: '',
      full_name: worker.full_name,
      email: worker.email || '',
      phone: worker.phone || '',
      status: worker.status,
      new_password: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingWorker(null);
    setShowPassword(false);
    setShowNewPassword(false);
    setPasswordStrength('');
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      status: 'active',
      new_password: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingWorker) {
        const updateData = {
          id: editingWorker.id,
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          status: formData.status
        };

        if (formData.new_password) {
          updateData.new_password = formData.new_password;
        }

        await updateWorker(updateData);
        setMessage({ type: 'success', text: 'Worker updated successfully!' });
      } else {
        await createWorker({
          username: formData.username,
          password: formData.password,
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone
        });
        setMessage({ type: 'success', text: 'Worker created successfully!' });
      }

      closeModal();
      fetchWorkerAccounts();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Operation failed' });
    }
  };

  const handleDeleteAccount = async (workerId, workerName) => {
    if (!window.confirm(`Are you sure you want to delete ${workerName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteWorker(workerId);
      setMessage({ type: 'success', text: 'Worker deleted successfully!' });
      fetchWorkerAccounts();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Delete failed' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateTimeEST(dateString);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('admin_user');
    if (onLogout) {
      onLogout();
    }
    navigate('/admin');
  };

  if (!user) {
    navigate('/admin');
    return null;
  }

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="admin-dashboard">
        <div className="page-header">
          <div>
            <h1 className="page-title">Workers Management</h1>
            <p className="page-subtitle">View statistics and manage worker accounts</p>
          </div>
          {activeTab === 'accounts' && (
            <button className="btn-create" onClick={openCreateModal}>
              + Create Worker
            </button>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <button
            onClick={() => setActiveTab('statistics')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'statistics' ? '#4db8ff' : 'transparent',
              color: activeTab === 'statistics' ? '#fff' : '#666',
              border: 'none',
              borderBottom: activeTab === 'statistics' ? '3px solid #4db8ff' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'statistics' ? '600' : '400',
              fontSize: '15px',
              transition: 'all 0.3s ease'
            }}
          >
            📊 Worker Statistics
          </button>
          <button
            onClick={() => setActiveTab('accounts')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'accounts' ? '#4db8ff' : 'transparent',
              color: activeTab === 'accounts' ? '#fff' : '#666',
              border: 'none',
              borderBottom: activeTab === 'accounts' ? '3px solid #4db8ff' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'accounts' ? '600' : '400',
              fontSize: '15px',
              transition: 'all 0.3s ease'
            }}
          >
            👤 Worker Accounts
          </button>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`alert alert-${message.type}`}>
            <div className="alert-content">
              {message.type === 'success' ? '✓' : '✕'} {message.text}
            </div>
            <button className="alert-close" onClick={() => setMessage({ type: '', text: '' })}>
              ×
            </button>
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'statistics' && (
          <>
            {error && (
              <div className="error-banner">
                <span>⚠</span> {error}
              </div>
            )}

            {loading ? (
              <WorkersSkeleton />
            ) : (
              <DataTable
                title="Worker Statistics"
                searchable={true}
                pagination={true}
                itemsPerPage={10}
                columns={[
                  {
                    key: '#',
                    label: '#',
                    sortable: false,
                    render: (_, __, index) => index + 1
                  },
                  {
                    key: 'worker_name',
                    label: 'Worker Name',
                    sortable: true
                  },
                  {
                    key: 'total_submissions',
                    label: 'Total Submissions',
                    sortable: true,
                    render: (value) => (
                      <span className="status-badge success">{value}</span>
                    )
                  },
                  {
                    key: 'total_hours',
                    label: 'Total Hours',
                    sortable: true,
                    render: (value) => `${value}h`
                  },
                  {
                    key: 'last_submission',
                    label: 'Last Submission',
                    sortable: true,
                    render: (value) => value ? formatDateEST(value) : '-'
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (_, row) => (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/admin/all-submissions?worker=' + encodeURIComponent(row.worker_name));
                          }}
                          className="table-action-btn primary"
                        >
                          View Submissions
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWorker(row.worker_name, row.total_submissions);
                          }}
                          className="table-action-btn danger"
                        >
                          Delete Worker
                        </button>
                      </div>
                    )
                  }
                ]}
                data={workers}
              />
            )}
          </>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <>
            {accountsLoading ? (
              <div className="loading-container">
                <div className="spinner-large"></div>
                <p>Loading worker accounts...</p>
              </div>
            ) : accountsError ? (
              <div className="error-container">
                <p>{accountsError}</p>
                <button className="btn-retry" onClick={fetchWorkerAccounts}>
                  Retry
                </button>
              </div>
            ) : (
              <div className="workers-table-container">
                <table className="workers-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Username</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workerAccounts.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="no-data">
                          No worker accounts found. Create your first worker account.
                        </td>
                      </tr>
                    ) : (
                      workerAccounts.map((worker, index) => (
                        <tr key={worker.id}>
                          <td>{index + 1}</td>
                          <td className="username-cell">{worker.username}</td>
                          <td>{worker.full_name}</td>
                          <td>{worker.email || '-'}</td>
                          <td>{worker.phone || '-'}</td>
                          <td>
                            <span className={`status-badge status-${worker.status}`}>
                              {worker.status}
                            </span>
                          </td>
                          <td className="date-cell">{formatDate(worker.last_login)}</td>
                          <td className="actions-cell">
                            <button
                              className="btn-action btn-edit"
                              onClick={() => openEditModal(worker)}
                              title="Edit worker"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-action btn-delete"
                              onClick={() => handleDeleteAccount(worker.id, worker.full_name)}
                              title="Delete worker"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingWorker ? 'Edit Worker' : 'Create New Worker'}</h2>
                <button className="modal-close" onClick={closeModal}>×</button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                {editingWorker && (
                  <div className="worker-info-section">
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Created:</span>
                        <span className="info-value">{formatDate(editingWorker.created_at)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Last Login:</span>
                        <span className="info-value">{formatDate(editingWorker.last_login)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <h3 className="section-title">Account Information</h3>
                  <div className="form-grid">
                    {!editingWorker && (
                      <div className="form-group full-width">
                        <label htmlFor="username" className="form-label">
                          Username <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="username"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                          autoFocus
                          placeholder="Enter username"
                        />
                        <span className="field-hint">Username cannot be changed later</span>
                      </div>
                    )}

                    {editingWorker && (
                      <div className="form-group full-width">
                        <label className="form-label">Username</label>
                        <input
                          type="text"
                          value={formData.username}
                          className="form-input"
                          disabled
                        />
                        <span className="field-hint">Username cannot be changed</span>
                      </div>
                    )}

                    {!editingWorker && (
                      <div className="form-group full-width">
                        <label htmlFor="password" className="form-label">
                          Password <span className="required">*</span>
                        </label>
                        <div className="password-field-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className="form-input password-input"
                            required
                            placeholder="Enter password"
                          />
                          <button
                            type="button"
                            className="btn-toggle-password"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? '👁️' : '👁️‍🗨️'}
                          </button>
                          {formData.password && (
                            <button
                              type="button"
                              className="btn-copy-password"
                              onClick={() => copyToClipboard(formData.password)}
                              title="Copy password"
                            >
                              📋
                            </button>
                          )}
                        </div>
                        <div className="password-actions">
                          <button
                            type="button"
                            className="btn-generate"
                            onClick={generatePassword}
                          >
                            🎲 Generate Password
                          </button>
                          {passwordStrength && (
                            <span className={`password-strength strength-${passwordStrength}`}>
                              {passwordStrength === 'weak' && '⚠️ Weak'}
                              {passwordStrength === 'medium' && '✓ Medium'}
                              {passwordStrength === 'strong' && '✓✓ Strong'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {editingWorker && (
                      <div className="form-group full-width">
                        <label htmlFor="new_password" className="form-label">
                          Reset Password
                        </label>
                        <div className="password-field-wrapper">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            id="new_password"
                            name="new_password"
                            value={formData.new_password}
                            onChange={handleInputChange}
                            className="form-input password-input"
                            placeholder="Leave blank to keep current password"
                          />
                          {formData.new_password && (
                            <>
                              <button
                                type="button"
                                className="btn-toggle-password"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                title={showNewPassword ? "Hide password" : "Show password"}
                              >
                                {showNewPassword ? '👁️' : '👁️‍🗨️'}
                              </button>
                              <button
                                type="button"
                                className="btn-copy-password"
                                onClick={() => copyToClipboard(formData.new_password)}
                                title="Copy password"
                              >
                                📋
                              </button>
                            </>
                          )}
                        </div>
                        <div className="password-actions">
                          <button
                            type="button"
                            className="btn-generate"
                            onClick={generatePassword}
                          >
                            🎲 Generate New Password
                          </button>
                          {passwordStrength && formData.new_password && (
                            <span className={`password-strength strength-${passwordStrength}`}>
                              {passwordStrength === 'weak' && '⚠️ Weak'}
                              {passwordStrength === 'medium' && '✓ Medium'}
                              {passwordStrength === 'strong' && '✓✓ Strong'}
                            </span>
                          )}
                        </div>
                        <span className="field-hint">⚠️ Worker will need to use the new password on next login</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="section-title">Personal Information</h3>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label htmlFor="full_name" className="form-label">
                        Full Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="Enter full name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    {editingWorker && (
                      <div className="form-group">
                        <label htmlFor="status" className="form-label">
                          Account Status <span className="required">*</span>
                        </label>
                        <select
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="active">✓ Active</option>
                          <option value="inactive">⊘ Inactive</option>
                          <option value="suspended">⛔ Suspended</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit">
                    {editingWorker ? '💾 Update Worker' : '➕ Create Worker'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Workers;
